import Vue from 'vue';

import { baConfig } from '../../../../config';
import { PrincipalsApi } from '../../../../generated/core-svc';

/**
 * Shared, cached lookup of org members by userId.
 *
 * Anyone who has a userId (e.g. `updateRequestedBy.userId`, audit rows, run history) and needs a
 * human-readable name should use this module instead of re-fetching principals:
 *   await this.$store.dispatch('orgUsers/loadOrgUsers');            // memoized per org
 *   const name = this.$store.getters['orgUsers/orgUserName'](userId);
 *
 * Names are sourced from core-svc's aggregated principals endpoint and cached per org for the
 * lifetime of the page, so the network call happens at most once per org.
 */
type OrgUsersState = {
  // orgId -> (userId -> display name)
  namesByOrg: Record<string, Record<string, string>>;
};

const MUT_SET_ORG_USER_NAMES = 'MUT_SET_ORG_USER_NAMES';

// In-flight requests keyed by orgId, so concurrent dispatches share a single network call.
// Kept out of Vuex state because promises are not serializable state.
const inFlight = new Map<string, Promise<void>>();

const state = (): OrgUsersState => ({
  namesByOrg: {},
});

const getters = {
  // Map of userId -> name for the currently selected org.
  orgUserNames: (state: OrgUsersState, _getters: any, rootState: any): Record<string, string> => {
    const orgId = rootState.currentOrg?.id;
    return (orgId && state.namesByOrg[orgId]) || {};
  },
  // Resolve a single userId to a display name (undefined if not loaded / not found).
  orgUserName:
    (_state: OrgUsersState, moduleGetters: any) =>
    (userId: string): string | undefined => {
      return moduleGetters.orgUserNames[userId];
    },
};

const mutations = {
  [MUT_SET_ORG_USER_NAMES](state: OrgUsersState, payload: { orgId: string; names: Record<string, string> }) {
    // Vue.set so a newly-added org key is reactive.
    Vue.set(state.namesByOrg, payload.orgId, payload.names);
  },
};

const actions = {
  // Loads and caches userId -> name for an org (defaults to the current org). Memoized: repeat
  // calls for an already-loaded org are a no-op, and concurrent calls share one request.
  async loadOrgUsers({ state, commit, rootState }: any, orgId?: string): Promise<void> {
    const id = orgId ?? rootState.currentOrg?.id;
    if (!id || state.namesByOrg[id]) return;
    if (inFlight.has(id)) return inFlight.get(id);

    const request = (async () => {
      try {
        const svc = new PrincipalsApi(undefined, `${baConfig.api3Url}/v3`);
        const resp = await svc.getOrgPrincipalsAggregated(id, undefined, 1000, { withCredentials: true });
        const names: Record<string, string> = {};
        for (const p of resp.data.items ?? []) {
          if (p.principalId && p.principalName) names[p.principalId] = p.principalName;
        }
        commit(MUT_SET_ORG_USER_NAMES, { orgId: id, names });
      } catch (e) {
        // Non-fatal: callers fall back to email/userId when a name can't be resolved.
        console.error('Failed to load org user names:', e);
      }
    })().finally(() => inFlight.delete(id));

    inFlight.set(id, request);
    return request;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
